import type { HelpStep } from "newui/components/ui/Help";
import type { ScopeType } from "newui/features/RolesPermissions/types/RolesPermissions.type";

/**
 * Author retest 2.3 / retest item 1+3 — `scope-results-project_type`/
 * `project`/`rfx` sit progressively further down the picker step, below
 * three independently paginated lists; once real data pushes ANY of them
 * past the fold, they live inside `Modal.tsx`'s scrollable `ModalBody`
 * (`scrollBehavior="inside"` → `overflow: auto`), which itself sits inside
 * Chakra's OWN modal dialog box (`ModalContent`, `position: relative`
 * hardcoded in `@chakra-ui/theme`) — which is in turn inside the true
 * full-viewport `position: fixed` container Chakra centers it in.
 *
 * react-joyride 2.9.3's spotlight math (`getElementPosition`, not exported —
 * verified by reading node_modules/react-joyride/dist/index.js directly)
 * computes a scrolled target's `top` as
 * `target.offsetTop - scrollParent.scrollTop`, correct only if
 * `target.offsetTop`'s reference point (the browser's own nearest
 * *positioned* ancestor, i.e. `offsetParent`) sits at viewport (0, 0).
 * `ModalContent` — vertically centered, not pinned to the viewport top —
 * used to be that reference point, silently dropping its own centering
 * offset from every reading (a first attempt at this traded the spotlight
 * away entirely via `placement: "center"`, since no react-joyride 2.9.3 prop
 * reaches this computation directly).
 *
 * Fixed at the root instead (`Modal.tsx`'s `neutralizeInternalPositioning`
 * prop, set on this flow's own `<Modal>` in `AddAccessFlow.tsx`): with
 * `ModalContent` no longer a positioned ancestor, `offsetParent` resolves
 * all the way out to the real `position: fixed`, `top: 0; left: 0` dialog
 * container — exactly the reference point react-joyride's formula assumes —
 * so `target.offsetTop - scrollParent.scrollTop` is correct regardless of
 * how far `ModalBody` has scrolled. See that prop's own doc comment for the
 * full diagnosis, including how the close button's own positioning is kept
 * intact without `ModalContent` itself staying positioned. All five steps
 * below are real spotlights again.
 *
 * Retest item 1 (the "Your selection" step below, `scope-chip-container`)
 * was never switched to `placement: "center"` — it stayed a real spotlight
 * throughout — but it hits this exact same `getElementPosition` bug (any
 * scrolled target inside this modal does), which is why it was reported
 * "too high and too short": a spotlight box drawn hundreds of pixels above
 * its true position happens to still overlap the TOP of the real element by
 * coincidence, while stopping well short of the "SPECIFIC RFX ACCESS"
 * section further down. No separate fix needed — `neutralizeInternalPositioning`
 * corrects the same `top` computation for every target in this modal.
 */
/**
 * PRO-757 follow-up — three of these steps target a specific per-type results
 * list (`scope-results-project_type` / `project` / `rfx`), and the picker no
 * longer renders all three for every company: a bidder-only company owns no
 * portfolios or projects and gets the RFx list alone. A step whose target is not
 * in the DOM is not a no-op in react-joyride — it leaves the tour pointing at
 * nothing — so the visible types are passed in and the steps for absent lists
 * are dropped.
 *
 * Defaults to all three so every existing caller and test is unchanged.
 */
export const buildCascadeHelpSteps = (
	visibleTypes: ScopeType[] = ["project_type", "project", "rfx"]
): HelpStep[] => {
	const steps: HelpStep[] = [
	{
		target: '[data-testid="scope-search-input"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Search across all three lists</h3>
				<p>
					Type a portfolio, project or RFx event name and every list below filters at once.
				</p>
			</div>
		),
		placement: "bottom",
		disableBeacon: true,
	},
	{
		target: '[data-testid="scope-results-project_type"]',
		placement: "right",
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Portfolios (project types)</h3>
				<p>
					The broadest specific level. Picking a portfolio grants the role for every project and
					RFx event inside it — now and in the future.
				</p>
			</div>
		),
		disableBeacon: true,
	},
	{
		target: '[data-testid="scope-results-project"]',
		placement: "right",
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Projects</h3>
				<p>
					Grants the role for one project and every RFx event inside it. A project already
					covered by a portfolio you picked shows &quot;Already Granted&quot; — picking it again
					would add nothing.
				</p>
			</div>
		),
		disableBeacon: true,
	},
	{
		target: '[data-testid="scope-results-rfx"]',
		placement: "right",
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">RFx events</h3>
				<p>The narrowest level — grants the role for a single RFx event only.</p>
			</div>
		),
		disableBeacon: true,
	},
	{
		target: '[data-testid="scope-chip-container"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Your selection</h3>
				<p>
					Everything picked this session shows here. Picking a portfolio after picking one of its
					projects replaces the narrower pick — you&apos;ll be asked to confirm first.
				</p>
			</div>
		),
		placement: "top",
		disableBeacon: true,
	},
	];

	return steps.filter((step) => {
		// Only the three per-type list steps are conditional; the search box and
		// the selection summary are always on screen.
		const listType = (["project_type", "project", "rfx"] as ScopeType[]).find(
			(type) => step.target === `[data-testid="scope-results-${type}"]`
		);

		return listType === undefined || visibleTypes.includes(listType);
	});
};
